// src/utils/validators.js

export const validateCurpResponse = (data) => {
    // Validamos basado en el JSON que me compartiste
    // "estatus": "OK" es la clave.
    if (data && data.estatus === "OK") {
        return { 
            isValid: true, 
            // Creamos un resumen limpio para el humano
            summary: { 
                "Nombre Completo": `${data.nombre} ${data.apellidoPaterno} ${data.apellidoMaterno}`,
                "CURP": data.curp,
                "Sexo": data.sexo,
                "Fecha de Nacimiento": data.fechaNacimiento,
                "Estado de Nacimiento": data.estadoNacimiento,
                "Estatus RENAPO": data.estatusCurp // RCN suele significar Registro Civil Nacional
            }
        };
    }
    
    // Si el estatus no es OK, buscamos el mensaje de error si existe
    const mensajeError = data.mensaje || "CURP no encontrado o estatus inválido.";
    return { isValid: false, message: mensajeError };
};

export const validateRfcResponse = (data) => {
    // (Este lo ajustaremos cuando tengas el JSON del RFC, 
    // por ahora dejo un placeholder lógico común)
    if (data && data.estatus === "Vigente") {
        return { 
            isValid: true, 
            summary: { 
                "RFC": data.rfc, 
                "Tipo": data.tipoPersona,
                "Estatus SAT": data.estatus
            }
        };
    }
    return { isValid: false, message: "RFC no válido o no encontrado." };
};

export const validateEmailResponse = (data) => {
    // CORRECCIÓN: La API devuelve los datos directos, no dentro de "data".
    // Por lo tanto, 'data' ya es el objeto con la info.

    // 1. Validar que llegó algo útil
    if (!data || !data.email) {
        return { isValid: false, message: "No se obtuvo información del correo." };
    }

    // 2. REGLA DE ORO: No aceptamos correos desechables
    // Validamos que exista 'domain_details' antes de preguntar por 'disposable'
    if (data.domain_details && data.domain_details.disposable) {
        return { 
            isValid: false, 
            message: "Correo rechazado: Es una dirección temporal/desechable." 
        };
    }

    // 3. ANALIZAR REDES SOCIALES
    const accounts = data.account_details || {};
    const activeNetworks = Object.keys(accounts).filter(networkName => {
        // Algunas APIs devuelven null, otras false. Buscamos strict true o registered true.
        return accounts[networkName] && accounts[networkName].registered === true;
    });

    const networksString = activeNetworks.length > 0 
        ? activeNetworks.map(n => n.charAt(0).toUpperCase() + n.slice(1)).join(", ") 
        : "Sin huella digital detectada";

    // 4. RETORNAR ÉXITO Y RESUMEN
    return {
        isValid: true,
        summary: {
            "Email Validado": data.email,
            "Score de Riesgo": data.score, // Ojo: A veces score bajo es bueno, depende de la API
            "Entregable": data.deliverable ? "SÍ" : "NO",
            "Dominio": data.domain_details ? data.domain_details.domain : "Desconocido",
            "Cuentas Detectadas": activeNetworks.length,
            "Plataformas": networksString
        }
    };
};

export const validatePhoneResponse = (data) => {
    // 1. Validar que llegó información
    if (!data) {
        return { isValid: false, message: "No se obtuvo información del teléfono." };
    }

    // 2. Validar existencia real del número
    if (data.valid === false) {
        return { 
            isValid: false, 
            message: "Número inválido: El teléfono no existe o tiene formato incorrecto." 
        };
    }

    // 3. Validar si es desechable (VoIP temporal, etc.)
    if (data.disposable === true) {
        return { 
            isValid: false, 
            message: "Número rechazado: Es un número temporal/desechable." 
        };
    }

    // 4. Analizar Redes Sociales (Huella Digital)
    const accounts = data.account_details || {};
    const activeNetworks = Object.keys(accounts).filter(networkName => {
        // Verificamos registered: true
        return accounts[networkName] && accounts[networkName].registered === true;
    });

    const networksString = activeNetworks.length > 0 
        ? activeNetworks.map(n => n.charAt(0).toUpperCase() + n.slice(1)).join(", ") 
        : "Sin huella digital detectada";

    // 5. Retornar Éxito y Resumen
    return {
        isValid: true,
        summary: {
            "Número": data.number,
            "País": data.country,
            "Operadora": data.carrier || "Desconocida",
            "Tipo Línea": data.type, // mobile, landline, voip
            "Score Riesgo": data.score,
            "Redes Sociales": networksString
        }
    };
};

export const validateIneResponse = (data) => {
    // 1. Validar que la API respondió algo coherente
    if (!data || !data.claveElector) {
        return { isValid: false, message: "No se pudieron leer los datos del documento. Intente con una foto más clara." };
    }

    // 2. VALIDACIÓN DE VIGENCIA (Regla de Negocio)
    const currentYear = new Date().getFullYear();
    const vigenciaIne = parseInt(data.vigencia, 10);

    // Si la vigencia es menor al año actual, está vencida.
    // Nota: Si vigencia es NaN (no leyó bien), asumimos riesgo.
    if (!isNaN(vigenciaIne) && vigenciaIne < currentYear) {
        return { 
            isValid: false, 
            message: `Documento VENCIDO. Vigencia: ${vigenciaIne}` 
        };
    }

    // 3. RETORNAR ÉXITO Y RESUMEN DETALLADO
    return {
        isValid: true,
        summary: {
            "Tipo Documento": data.tipo, // IFE, INE
            "Clave Elector": data.claveElector,
            "CURP Extraída": data.curp,
            "Nombre Completo": `${data.nombres} ${data.primerApellido} ${data.segundoApellido}`,
            "Domicilio": `${data.calle}, ${data.colonia}`,
            "Ciudad/Estado": `${data.ciudad}, ${data.estado}`,
            "Vigencia": data.vigencia,
            "Edad": data.edad,
            "Sexo": data.sexo
        }
    };
};

export const validateDomainResponse = (data) => {
    // 1. Verificamos si trajo detalles del dominio
    if (!data || !data.domain_details) {
        return { isValid: false, message: "No se encontró información del dominio." };
    }

    const details = data.domain_details;

    // 2. REGLA DE LEGITIMIDAD: ¿Existe el sitio web?
    if (details.website_exists === false) {
        return { 
            isValid: false, // O true con advertencia, dependiendo de tu criterio
            message: "Alerta: El dominio está registrado pero NO tiene sitio web activo." 
        };
    }

    // 3. REGLA DE ANTIGÜEDAD (Cálculo simple)
    // "created": "1995-08-13 04:00:00"
    let antiguedad = "Desconocida";
    if (details.created) {
        const fechaCreacion = new Date(details.created);
        const hoy = new Date();
        const anios = hoy.getFullYear() - fechaCreacion.getFullYear();
        antiguedad = `${anios} años (${details.created.split(' ')[0]})`;
        
        // Si el dominio tiene menos de 3 meses, es SOSPECHOSO
        if (anios === 0 && (hoy.getMonth() - fechaCreacion.getMonth() < 3)) {
             return { isValid: true, summary: { ...details, Alerta: "DOMINIO MUY NUEVO (Posible Fraude)" } };
        }
    }

    // 4. RETORNAR RESUMEN DE DOMINIO
    return {
        isValid: true,
        summary: {
            "Dominio": details.domain,
            "Sitio Web Activo": details.website_exists ? "SÍ ✅" : "NO ❌",
            "Antigüedad": antiguedad,
            "Vence en": details.expires ? details.expires.split(' ')[0] : "N/A",
            "Registrador": details.registrar_name || "N/A",
            "Correo Desechable": details.disposable ? "SÍ ⚠️" : "NO",
            "Servidor Correo": details.valid_mx ? "Configurado ✅" : "Sin MX ❌"
        }
    };
};